/**
 * Service responsável por orquestra as 7 chamadas de API sequenciais
 * para criar a estrutura de onboarding (Profissional, Serviço, Local, etc)
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * Classe para gerenciar o processo de onboarding
 */
class OnboardingService {
  constructor(token) {
    this.token = token;
    this.headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  /**
   * Delay entre chamadas de API (em ms)
   */
  delay(ms = 3000) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Faz uma chamada de API com tratamento de erro
   */
  async apiCall(method, endpoint, data = null) {
    try {
      const url = `${API_BASE_URL}${endpoint}`;
      const options = {
        method,
        headers: this.headers,
      };

      if (data) {
        options.body = JSON.stringify(data);
      }

      console.log(`[OnboardingService] Chamando: ${method} ${url}`);
      const response = await fetch(url, options);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `API Error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error(`[OnboardingService] API Error: ${method} ${endpoint}`, error);
      throw error;
    }
  }

  /**
   * 1. Criar Profissional
   */
  async createProfessional(professionalName) {
    console.log('[OnboardingService] Etapa 1/7: Criando profissional...');
    const response = await this.apiCall('POST', '/professionals', {
      name: professionalName,
    });
    await this.delay();
    return response.data || response;
  }

  /**
   * 2. Criar Serviço
   */
  async createService(serviceName, template) {
    console.log('[OnboardingService] Etapa 2/7: Criando serviço...');
    const categoryName = template.serviceCategories?.[0]?.name || 'Geral';
    const response = await this.apiCall('POST', '/services', {
      name: serviceName,
      category: categoryName,
      duration: template.onboarding?.defaultServiceDuration || 30,
      price: template.onboarding?.defaultServicePrice || 0,
    });
    await this.delay();
    return response.data || response;
  }

  /**
   * 3. Criar Local
   */
  async createPlace(placeName) {
    console.log('[OnboardingService] Etapa 3/7: Criando local...');
    const response = await this.apiCall('POST', '/places', {
      name: placeName,
    });
    await this.delay();
    return response.data || response;
  }

  /**
   * 4. Criar Vínculo Place-Service
   */
  async createPlaceService(placeId, serviceId) {
    console.log('[OnboardingService] Etapa 4/8: Vinculando serviço ao local...');
    const response = await this.apiCall('POST', `/places/${placeId}/services`, {
      serviceId,
    });
    await this.delay();
    return response.data || response;
  }

  /**
   * 5. Criar Vínculo Place-Professional
   */
  async createPlaceProfessional(placeId, professionalId) {
    console.log('[OnboardingService] Etapa 5/8: Vinculando profissional ao local...');
    const response = await this.apiCall('POST', `/places/${placeId}/professionals`, {
      professionalId,
    });
    await this.delay();
    return response.data || response;
  }

  /**
   * 6. Vincular Profissional ao Serviço no Local (Place-Service-Professional)
   * Replicar exatamente o que o usuário faz manualmente no admin:
   * POST /api/places/{placeId}/services/{serviceId}/professionals
   * Body: { placeProfessionalId }
   */
  async createPlaceServiceProfessional(placeId, placeServiceId, placeProfessionalId) {
    console.log('[OnboardingService] Etapa 6/8: Vinculando profissional ao serviço no local...');
    const response = await this.apiCall('POST', `/places/${placeId}/services/${placeServiceId}/professionals`, {
      placeProfessionalId,
    });
    await this.delay();
    return response.data || response;
  }

  /**
   * 7. Criar Agenda (Schedule)
   */
  async createSchedule(placeId, professionalId, name) {
    console.log('[OnboardingService] Etapa 7/8: Criando agenda...');
    const response = await this.apiCall('POST', '/schedules', {
      placeId,
      professionalId,
      name: name || 'Agenda Padrão',
    });
    await this.delay();
    return response.data || response;
  }

  /**
   * 8. Criar Turnos (Shifts) - 2 turnos padrão
   */
  async createScheduleShifts(scheduleId, template, serviceIds = []) {
    console.log('[OnboardingService] Etapa 8/8: Criando turnos...');
    const shifts = template.onboarding?.defaultScheduleShifts || [
      {
        daysOfWeek: [1, 2, 3, 4, 5],
        startTime: '09:00',
        endTime: '12:00',
        name: 'Turno da Manhã',
      },
      {
        daysOfWeek: [1, 2, 3, 4, 5],
        startTime: '13:00',
        endTime: '18:00',
        name: 'Turno da Tarde',
      },
    ];

    const createdShifts = [];
    for (const shift of shifts) {
      try {
        // Converter daysOfWeek array para propriedades booleanas
        // daysOfWeek: [1,2,3,4,5] = [segunda, terça, quarta, quinta, sexta]
        const shiftData = {
          scheduleId,
          startTime: shift.startTime,
          endTime: shift.endTime,
          name: shift.name,
          monday: shift.daysOfWeek?.includes(1) ?? false,
          tuesday: shift.daysOfWeek?.includes(2) ?? false,
          wednesday: shift.daysOfWeek?.includes(3) ?? false,
          thursday: shift.daysOfWeek?.includes(4) ?? false,
          friday: shift.daysOfWeek?.includes(5) ?? false,
          saturday: shift.daysOfWeek?.includes(6) ?? false,
          sunday: shift.daysOfWeek?.includes(7) ?? false,
          serviceIds,
        };

        const response = await this.apiCall(
          'POST',
          `/schedules/${scheduleId}/shifts`,
          shiftData
        );
        createdShifts.push(response.data || response);
      } catch (error) {
        console.warn('[OnboardingService] Erro ao criar turno:', error);
        // Continua mesmo se algum turno falhar
      }
    }

    return createdShifts;
  }

  /**
   * 8. Atualizar Tema do Usuário
   */
  async updateUserTheme(themeId) {
    console.log('[OnboardingService] Etapa 8/8: Atualizando tema do usuário...');
    
    // Mapear ID do tema para o enum ThemeType (1-indexed, conforme ThemeType.cs)
    const themeMap = {
      'default': 1,
      'barbershop': 2,
      'clinic': 3,
      'tattoo-studio': 4,
      'beauty-salon': 5,
      'psychology': 6,
      'aesthetics': 7,
      'massage': 8,
      'dental': 9,
      'medical': 10,
      'therapy': 11,
      'teacher': 12,
      'personal-trainer': 13,
    };

    const themeEnum = themeMap[themeId];
    if (themeEnum === undefined) {
      console.warn('[OnboardingService] Tema não encontrado no mapa:', themeId);
      return null;
    }

    const response = await this.apiCall('PUT', '/users/theme', {
      theme: themeEnum,
    });
    await this.delay();
    return response.data || response;
  }

  /**
   * Orquestra todo o processo de onboarding
   * Executa as 8 etapas sequencialmente
   * Segue EXATAMENTE as mesmas APIs que o usuário usa no admin frontend
   */
  async createOnboardingStructure(responses, template, onStepComplete) {
    const { professionalName, placeName, serviceName } = responses;
    const createdIds = {};
    let stepIndex = 0;

    try {
      // Etapa 1: Profissional
      const professional = await this.createProfessional(professionalName);
      createdIds.professionalId = professional.id;
      if (onStepComplete) onStepComplete(stepIndex++);

      // Etapa 2: Serviço
      const service = await this.createService(serviceName, template);
      createdIds.serviceId = service.id;
      if (onStepComplete) onStepComplete(stepIndex++);

      // Etapa 3: Local
      const place = await this.createPlace(placeName);
      createdIds.placeId = place.id;
      if (onStepComplete) onStepComplete(stepIndex++);

      // Etapa 4: Vínculo Place-Service (primeiro vincular serviço ao local)
      const placeService = await this.createPlaceService(
        place.id,
        service.id
      );
      createdIds.placeServiceId = placeService.id;
      if (onStepComplete) onStepComplete(stepIndex++);

      // Etapa 5: Vínculo Place-Professional (depois vincular profissional ao local)
      const placeProfessional = await this.createPlaceProfessional(
        place.id,
        professional.id
      );
      createdIds.placeProfessionalId = placeProfessional.id;
      if (onStepComplete) onStepComplete(stepIndex++);

      // Etapa 6: Vincular Profissional ao Serviço no Local (Place-Service-Professional)
      // Esta é a API que o usuário chama manualmente no admin
      const placeServiceProfessional = await this.createPlaceServiceProfessional(
        place.id,
        placeService.id,
        placeProfessional.id
      );
      createdIds.placeServiceProfessionalId = placeServiceProfessional.id;
      if (onStepComplete) onStepComplete(stepIndex++);

      // Etapa 7: Agenda
      const schedule = await this.createSchedule(
        place.id,
        professional.id,
        `Agenda de ${professionalName}`
      );
      createdIds.scheduleId = schedule.id;
      if (onStepComplete) onStepComplete(stepIndex++);

      // Etapa 8: Turnos
      const shifts = await this.createScheduleShifts(schedule.id, template, [service.id]);
      createdIds.shiftIds = shifts.map((s) => s.id);
      if (onStepComplete) onStepComplete(stepIndex++);

      // Gerar link público
      const publicLink = `https://share.agendario.com.br/schedule/${schedule.id}`;

      return {
        success: true,
        createdIds,
        publicLink,
        entities: {
          professional,
          service,
          place,
          schedule,
        },
      };
    } catch (error) {
      console.error('[OnboardingService] Erro no processo de onboarding:', error);
      throw error;
    }
  }
}

export default OnboardingService;
